import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PLANS, OFFER_SHORT, formatPrice, TIMETABLE,
  SHOW_TESTIMONIALS, TESTIMONIALS,
} from '../config/siteConfig';
import WeekTimetable from '../components/layout/WeekTimetable';
import '../styles/brand.css';
import './LandingPage.css';

// Landing page — matches docs/redesign/reference-landing.html.
// Sign-up CTAs go to /register, log-in to /login, students to /child/login.

const GLYPH = { k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟' };

// Static board preview. Cells are a fixed 8×8 grid (grid-template-rows +
// aspect-ratio: 1), so every square is exactly square at any width.
function MiniBoard({ fen, marks = {}, label }) {
  const cells = [];
  fen.split('/').forEach((row, ri) => {
    let f = 0;
    for (const c of row) {
      const n = /\d/.test(c) ? +c : 0;
      const pieces = n ? Array(n).fill(null) : [c];
      for (const p of pieces) {
        const sq = 'abcdefgh'[f] + (8 - ri);
        cells.push(
          <span key={sq} className={`${(ri + f) % 2 ? 'd' : 'l'} ${marks[sq] || ''}`}>
            {p && <i className={p === p.toUpperCase() ? 'w' : 'b'}>{GLYPH[p.toLowerCase()]}{'︎'}</i>}
          </span>
        );
        f++;
      }
    }
  });
  return <div className="mini" role="img" aria-label={label}>{cells}</div>;
}

function TwoBoards({ say, tutor, student }) {
  return (
    <>
      <div className="pv-top">
        <img className="pv-owl" src="/images/momo-owl.png" alt="Ms. Momo" />
        <div className="pv-say">{say}</div>
      </div>
      <div className="pv-boards">
        <div>
          <div className="pv-label">🦉 Ms. Momo</div>
          <div className="frame-tutor"><MiniBoard {...tutor} /></div>
        </div>
        <div>
          <div className="pv-label">🙋 Your board</div>
          <div className="frame-you"><MiniBoard {...student} /></div>
        </div>
      </div>
    </>
  );
}

const SUBJECTS = [
  { name: 'Chess', icon: '♟️', tint: 'var(--t-green)', live: true,
    desc: '24 guided lessons from the board to real strategy, all through play.',
    points: ['Board, files, ranks and squares', 'How every piece moves', 'Captures, tactics and checkmate', 'Openings and endgames'] },
  { name: 'Coding', icon: '💻', tint: 'var(--t-lav)',
    desc: 'From blocks to real code: children build games, animations and apps.',
    points: ['Logic and sequencing', 'Loops, conditions and functions', 'First real projects', 'Web and game basics'] },
  { name: 'Typing', icon: '⌨️', tint: 'var(--t-peach)',
    desc: 'Confident, fast typing that makes every other skill easier.',
    points: ['Home row and finger placement', 'Speed and accuracy drills', 'Punctuation and shortcuts', 'Real-world speed tests'] },
];

const FAQ = [
  ['What ages is Talent School Africa for?', 'Children aged 6 to 15. Younger children (6–8) learn with Ms. Momo’s voice and visual demonstrations, with light help from a parent. Children 9 and up usually learn fully on their own. Ms. Momo adjusts her tone to each age group.'],
  ['Does my child need to know chess already?', 'Not at all. Lesson 1 starts with the board itself: files, ranks and squares. Every piece is introduced slowly, demonstrated by Ms. Momo, then practised.'],
  ['How does "do as I do" work?', 'Ms. Momo makes each move on her own board while she explains it. Then your child makes the same move on their board. New topics come with hints; quizzes and challenges test what your child remembers.'],
  ['What device do we need?', 'A laptop or desktop computer with a modern browser (Chrome recommended) and speakers or headphones. The two-board lesson screen is designed for a larger screen.'],
  ['How long is each lesson?', 'About 55–60 minutes, including a warm-up and a bonus round. Children can pause at any time and pick up where they left off.'],
  ['How much does it cost?', `Your first month is free. After that, Scholar is ${formatPrice(PLANS[0].monthly)} a month for one child, and Family is ${formatPrice(PLANS[1].monthly)} a month for 2 to 3 children.`],
  ['Can I cancel any time?', 'Yes. There are no contracts, and the first month is free. You can cancel at any time.'],
];

function useReveal(rootRef) {
  useEffect(() => {
    const els = rootRef.current?.querySelectorAll('.rv') || [];
    if (!('IntersectionObserver' in window)) { els.forEach(el => el.classList.add('in')); return; }
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    }), { threshold: 0.12 });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [rootRef]);
}

export default function LandingPage() {
  const rootRef = useRef(null);
  const [openQ, setOpenQ] = useState(null);
  useReveal(rootRef);

  useEffect(() => { document.title = 'Talent School Africa'; }, []);

  const quotes = SHOW_TESTIMONIALS ? TESTIMONIALS.filter(t => !t.placeholder) : [];

  return (
    <div className="tsa-brand tsa-landing" ref={rootRef}>
      <header className="nav">
        <div className="wrap nav-in">
          <a href="#top" className="nav-logo" aria-label="Talent School Africa home">
            <img src="/images/logo-full.png" alt="Talent School Africa — Discover, Learn, Build Your Future" />
          </a>
          <nav className="nav-links" aria-label="Main">
            <a href="#subjects">What We Teach</a>
            <a href="#how">How It Works</a>
            <a href="#week">Timetable</a>
            <a href="#momo">Ms. Momo</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="nav-cta">
            <Link className="btn btn-ghost" to="/login">Log in</Link>
            <Link className="btn btn-green" to="/register">Start free</Link>
          </div>
        </div>
      </header>

      <main id="top">
        {/* HERO */}
        <section className="hero">
          <div className="cloud" style={{ width: 120, height: 30, left: '8%', top: 60 }} />
          <div className="cloud" style={{ width: 80, height: 22, left: '12%', top: 48 }} />
          <div className="cloud" style={{ width: 140, height: 34, right: '10%', top: 90 }} />
          <div className="wrap hero-in">
            <div>
              <span className="pill"><b>NEW</b> Chess is live · Coding &amp; Typing coming soon</span>
              <h1>Skills that help your child <em>build their future</em></h1>
              <p className="lead">Talent School Africa teaches Chess, Coding and Typing to children aged 6–15. Ms. Momo, our AI tutor, shows each move on her board, then your child does it on theirs.</p>
              <div className="hero-ctas">
                <Link className="btn btn-green" to="/register">Start your free month →</Link>
                <a className="btn btn-white" href="#how">See how it works</a>
              </div>
              <div className="hero-stats">
                <div>24<span>Chess lessons</span></div>
                <div>6–15<span>Ages</span></div>
                <div>3<span>Core skills</span></div>
                <div>1 month<span>Free to start</span></div>
              </div>
            </div>
            <div className="preview rv" aria-label="Preview of a lesson: Ms. Momo's board and the student's board">
              <div className="pv-badge">Do as I do!</div>
              <TwoBoards
                say={<>“Watch my pawn go <b>e2 to e4</b>. Now you try!”</>}
                tutor={{ fen: 'rnbqkbnr/pppppppp/8/8/4P3/8/PPPP1PPP/RNBQKBNR', marks: { e2: 'hl', e4: 'hl' }, label: "Ms. Momo's board: pawn moved from e2 to e4" }}
                student={{ fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR', marks: { e2: 'go' }, label: "Student's board: starting position, e2 pawn ready to move" }}
              />
              <div className="pv-badge2">⭐ +10 points · First Hit!</div>
            </div>
          </div>
          <div className="hero-scene" aria-hidden="true">
            <svg viewBox="0 0 1440 200" preserveAspectRatio="none">
              <path d="M0 90 C 200 30, 380 50, 560 100 S 920 80, 1100 90 S 1340 50, 1440 70 V200 H0Z" fill="#B4DE96" />
              <path d="M0 125 C 160 80, 320 90, 500 140 S 860 160, 1040 135 S 1300 90, 1440 110 V200 H0Z" fill="#7CC667" />
              <path d="M0 110 C 120 90, 260 105, 400 170 L 400 200 H0Z" fill="#0B9A3C" opacity=".85" />
              <path d="M1440 100 C 1320 90, 1180 120, 1060 180 L 1060 200 H1440Z" fill="#0B9A3C" opacity=".85" />
            </svg>
          </div>
        </section>

        {/* SUBJECTS */}
        <section className="sec" id="subjects">
          <div className="wrap">
            <div className="sec-head rv">
              <span className="eyebrow">What we teach</span>
              <h2>Three skills. One bright future.</h2>
              <p>Every lesson builds focus, problem-solving and confidence, skills children carry into school and life.</p>
            </div>
            <div className="subjects">
              {SUBJECTS.map(s => (
                <article key={s.name} className={`subj rv ${s.live ? '' : 'subj-soon'}`}>
                  <span className={`tag ${s.live ? 'live' : 'soon'}`}>{s.live ? 'Live now' : 'Coming soon'}</span>
                  <div className="subj-ic" style={{ background: s.tint }}>{s.icon}</div>
                  <h3>{s.name}</h3>
                  <p>{s.desc}</p>
                  <ul>{s.points.map(p => <li key={p}>{p}</li>)}</ul>
                  {s.live
                    ? <Link className="subj-more" to="/subject/chess">Explore the chess course →</Link>
                    : <span className="subj-more subj-more-off" aria-disabled="true">Coming soon</span>}
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="sec how" id="how">
          <div className="wrap">
            <div className="sec-head rv">
              <span className="eyebrow">How it works</span>
              <h2>Discover. Learn. Build your future.</h2>
              <p>Every lesson follows the way children learn best: they watch, they do, then they prove it.</p>
            </div>
            <div className="steps">
              <div className="step rv"><div className="step-n" style={{ background: 'var(--navy)' }}>1</div><h3>Watch Ms. Momo</h3><p>She explains in a friendly voice and shows each move on her own board, square by square.</p></div>
              <div className="step rv"><div className="step-n" style={{ background: 'var(--green)' }}>2</div><h3>Do it yourself</h3><p>Your child copies the move on their board. New topics come with gentle hints; mistakes get a replay, never a scolding.</p></div>
              <div className="step rv"><div className="step-n" style={{ background: 'var(--yellow-2)' }}>3</div><h3>Prove it and earn</h3><p>Quizzes and challenges test what stuck. Points, badges and streaks make every lesson feel like a win.</p></div>
            </div>
          </div>
        </section>

        {/* WEEKLY TIMETABLE (config/siteConfig.js TIMETABLE) */}
        <section className="sec" id="week">
          <div className="wrap">
            <div className="sec-head rv">
              <span className="eyebrow">A week at Talent School</span>
              <h2>One subject a day. Rest on Sunday.</h2>
              <p>Every session starts with a 10-minute typing warm-up, then about 50 minutes with Ms. Momo. {TIMETABLE[6].label} on Saturdays brings it all together: {TIMETABLE[6].detail.split(': ')[1]}.</p>
            </div>
            <div className="rv"><WeekTimetable large /></div>
          </div>
        </section>

        {/* MS MOMO */}
        <section className="sec" id="momo">
          <div className="wrap momo-sec">
            <div className="rv">
              <span className="eyebrow">Meet your tutor</span>
              <h2 className="momo-h">Ms. Momo shows. Your child does.</h2>
              <p className="momo-lead">Children follow what they can see. Ms. Momo never just talks; she demonstrates on her board, then hands over to your child.</p>
              <div className="feat">
                <div className="feat-item"><div className="feat-ic" style={{ background: 'var(--t-blue)' }}>🎙️</div><div><h3>Voice for every step</h3><p>Children who can't read fluently yet can still learn on their own.</p></div></div>
                <div className="feat-item"><div className="feat-ic" style={{ background: 'var(--t-green)' }}>🧭</div><div><h3>Hints when it's new, quiet when it's a test</h3><p>She guides on brand-new topics and steps back for quizzes, so learning sticks.</p></div></div>
                <div className="feat-item"><div className="feat-ic" style={{ background: 'var(--t-peach)' }}>💛</div><div><h3>Patient, every time</h3><p>Wrong move? She simply shows it again. Effort is celebrated, not just results.</p></div></div>
              </div>
            </div>
            <div className="dido rv">
              <TwoBoards
                say={<>“Pawns <b>capture diagonally</b>. Copy my move!”</>}
                tutor={{ fen: '4k3/8/8/3P4/8/8/8/4K3', marks: { e4: 'hl', d5: 'hl' }, label: "Ms. Momo's board: white pawn captured on d5" }}
                student={{ fen: '4k3/8/8/3p4/4P3/8/8/4K3', marks: { e4: 'go' }, label: "Student's board: white pawn on e4 ready to capture on d5" }}
              />
              <div className="dido-steps">
                <div className="dido-step" style={{ background: 'var(--t-blue)' }}>1. Demonstrate<span>on her board</span></div>
                <div className="dido-step" style={{ background: 'var(--t-green)' }}>2. Instruct<span>you copy it</span></div>
                <div className="dido-step" style={{ background: 'var(--yellow-bg)' }}>3. Test<span>quiz and challenge</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* REWARDS */}
        <section className="sec" style={{ background: '#fff' }}>
          <div className="wrap">
            <div className="sec-head rv"><span className="eyebrow">Progress and rewards</span><h2>Learning that feels like winning</h2></div>
            <div className="rewards">
              <div className="rw rv" style={{ background: 'var(--t-green)' }}><div className="rw-ic">🎯</div><h3>Badges</h3><p>First Hit, On Fire, Speed Star and more: a growing collection of real skills.</p></div>
              <div className="rw rv" style={{ background: 'var(--t-peach)' }}><div className="rw-ic">🔥</div><h3>Daily streaks</h3><p>Small daily practice becomes a lasting habit.</p></div>
              <div className="rw rv" style={{ background: 'var(--t-lav)' }}><div className="rw-ic">📜</div><h3>Certificates</h3><p>Finish a course and earn a certificate worth framing.</p></div>
              <div className="rw rv" style={{ background: 'var(--t-blue)' }}><div className="rw-ic">📈</div><h3>Parent reports</h3><p>See lessons finished, time spent and skills mastered, at a glance.</p></div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS — hidden unless SHOW_TESTIMONIALS is on AND real quotes exist */}
        {quotes.length > 0 && (
          <section className="sec">
            <div className="wrap">
              <div className="sec-head rv"><span className="eyebrow">What parents say</span><h2>Families love it</h2></div>
              <div className="quotes">
                {quotes.map(q => (
                  <figure key={q.name} className="quote rv">
                    <div className="stars" aria-label="5 stars">★★★★★</div>
                    <p>“{q.quote}”</p>
                    <figcaption className="who">
                      <div className="who-av" style={{ background: 'var(--green)' }}>{q.name[0]}</div>
                      <div><b>{q.name}</b><span>{q.detail}</span></div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* PRICING */}
        <section className="sec pricing" id="pricing">
          <div className="wrap">
            <div className="sec-head rv">
              <span className="eyebrow">Simple pricing</span>
              <h2>Your first month is free.</h2>
              <p>Cancel anytime. No hidden fees. Every plan includes Ms. Momo.</p>
            </div>
            <div className="plans plans-2">
              {PLANS.map(p => (
                <div key={p.id} className={`plan rv ${p.popular ? 'pop' : ''}`}>
                  {p.popular && <div className="pop-tag">Best for families</div>}
                  <h3>{p.name} <span className="plan-kids">{p.children}</span></h3>
                  <div className="price">{formatPrice(p.monthly)} <small>/ month</small></div>
                  <p className="plan-after">after your free first month</p>
                  <p className="note">{p.note}</p>
                  <ul>{p.features.map(t => <li key={t} className="y">{t}</li>)}</ul>
                  <Link className={`btn ${p.popular ? 'btn-green' : 'btn-white'}`} to="/register">Start your free month</Link>
                </div>
              ))}
            </div>
            <p className="bill-note">Prices in Nigerian Naira, billed monthly. Cancel anytime.</p>
          </div>
        </section>

        {/* TRUST */}
        <section className="sec">
          <div className="wrap">
            <div className="sec-head rv"><span className="eyebrow">Why parents trust us</span><h2>Safe, purposeful, proven</h2></div>
            <div className="trust">
              <div className="tr rv"><div className="tr-ic">🔒</div><h3>Child-safe</h3><p>No chat with strangers, no ads, no outside links. Just focused learning.</p></div>
              <div className="tr rv"><div className="tr-ic">💻</div><h3>Built for laptops and desktops</h3><p>A big, clear screen with two boards side by side, made for real practice.</p></div>
              <div className="tr rv"><div className="tr-ic">👨‍👩‍👧</div><h3>Parent dashboard</h3><p>Lessons completed, time spent and badges earned, all in one place.</p></div>
              <div className="tr rv"><div className="tr-ic">🎓</div><h3>Structured curriculum</h3><p>24 carefully sequenced lessons, each building on the last.</p></div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="sec" id="faq" style={{ background: '#fff' }}>
          <div className="wrap">
            <div className="sec-head rv"><span className="eyebrow">Questions</span><h2>Frequently asked</h2></div>
            <div className="faq">
              {FAQ.map(([q, a], i) => (
                <div key={q} className="qa">
                  <button type="button" aria-expanded={openQ === i} aria-controls={`ans${i}`} onClick={() => setOpenQ(openQ === i ? null : i)}>
                    {q}<span aria-hidden="true">+</span>
                  </button>
                  <div className="ans" id={`ans${i}`} hidden={openQ !== i}><p>{a}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="sec">
          <div className="wrap">
            <div className="final rv">
              <img className="final-owl" src="/images/momo-owl.png" alt="" />
              <div><h2>Ready to discover your child's talent?</h2><p>{OFFER_SHORT}. Cancel anytime.</p></div>
              <Link className="btn btn-green final-btn" to="/register">Start free today →</Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="foot">
        <div className="wrap">
          <div className="foot-in">
            <div>
              <img src="/images/logo-full.png" alt="Talent School Africa" />
              <p className="small">Chess, Coding and Typing for children aged 6–15. A Capitaro Company Limited product, Lagos, Nigeria.</p>
            </div>
            <div><h4>Learn</h4><ul>
              <li><Link to="/subject/chess">Chess</Link></li>
              <li><span className="foot-soon">Coding · coming soon</span></li>
              <li><span className="foot-soon">Typing · coming soon</span></li>
            </ul></div>
            <div><h4>Parents</h4><ul>
              <li><a href="#pricing">Pricing</a></li>
              <li><a href="#faq">FAQ</a></li>
              <li><Link to="/login">Parent login</Link></li>
              <li><Link to="/register">Create an account</Link></li>
            </ul></div>
            <div><h4>Students</h4><ul>
              <li><Link to="/child/login">Student login</Link></li>
              <li><a href="#how">How lessons work</a></li>
              <li><a href="#momo">Meet Ms. Momo</a></li>
            </ul></div>
          </div>
          <div className="foot-bot"><span>© 2026 Talent School Africa. All rights reserved.</span><span>Discover • Learn • Build Your Future</span></div>
        </div>
      </footer>
    </div>
  );
}
